<template lang="pug">
.appointment-calendar-right-panel.mr-3.d-flex.flex-column(style="height: calc(100vh - 72px)" :class="{ 'mobile-view': isMobileView }")
  .unassigned-banner.d-flex.align-center.mx-4.mt-4.flex-shrink-0(v-if="hqUnassignedCount > 0")
    yz-icon.mr-2(size="20" color="danger500") $yzi-alert-circle-solid
    .yz-medium-body-sm.flex-grow-1.text-neutral-700 HQ service unassigned
    yz-btn.ml-3(label="View" outlined background-color="white" size="sm" @click="openCancelledAppointmentsDialog = true")
  //- No appointment selected
  .flex-grow-1.overflow-y-auto(v-if="!selectedId || isNotFound" style="min-height:0")
    .px-5.py-6
      template(v-if="isNotFound")
        v-alert.pa-4.danger500.mb-4.rounded-lg(dark dismissible)
          .text-sm.white--text Appointment {{ selectedId }} not found
      //- Search
      appointment-calendar-search-dropdown.mb-8(@selected="onSelectedAppointment")
      //- Description
      .mb-8
        .text-sm.font-semibold.mb-2 No selected events
        .text-xs.mb-2 Click on any appointment in the calendar to view its details here instantly.
        .text-xs To create a new appointment, use the "Add appointment" button on the calendar.
      //- Add appointment
      v-btn.button-sm.white--text.add-appointment-button(depressed color="neutral700" @click="openAddAppointmentDialog = true") Add appointment
  .flex-grow-1.overflow-y-auto.pb-15(v-else style="min-height:0")
    //- Profile
    .px-5.py-6
      template(v-if="isFetching")
        v-skeleton-loader(type="text@3")
      template(v-else-if="isError")
        v-card.pa-4.danger--border(outlined)
          .text-sm.danger500--text Opss.. An error occured
      template(v-else)
        //- Name
        .text-sm.font-semibold.mb-2 {{ patient.name }}
        //- Phone
        .d-flex.justify-space-between.align-center
          div
            .text-xs.neutral500--text.font-medium Phone number 
            .text-sm {{ patient.phone }} 
          v-btn.button-sm(depressed :disabled="!patient.phone" @click="onSendWhatsapp()") Send WhatsApp

    v-divider

    //- Details
    .px-5.py-6
      .text-sm.font-semibold.mb-2 Details
      //- Date
      .d-flex.align-center.mb-3
        v-icon.mr-2(size="16") $yzi-calendar
        template(v-if="isFetching")
          v-skeleton-loader.grow.no-margin(type="text")
        template(v-else-if="isError")
          .text-sm.danger500--text Opss.. An error occured
        template(v-else)
          .text-sm(v-if="appointment && !appointment.combo_set.length") {{ weekday(getAppointmentDateTime(appointment)) }}, {{ displayDate(getAppointmentDateTime(appointment)) }}
          .text-sm(v-else) {{ weekday(getComboAppointmentDateTime(appointment)) }}, {{ displayDate(getComboAppointmentDateTime(appointment)) }}
      //- Time
      .d-flex.align-center.mb-3
        v-icon.mr-2(size="16") $yzi-clock
        template(v-if="isFetching")
          v-skeleton-loader.grow.no-margin(type="text")
        template(v-else-if="isError")
          .text-sm.danger500--text Opss.. An error occured
        template(v-else)
          template(v-if="appointment && !appointment.combo_set.length")
            .text-sm.mr-2 {{ displayTime(getAppointmentDateTime(appointment)) }}
            .text-sm.neutral500--text {{ displayDuration(getAppointmentDateTime(appointment)) }}
          template(v-else)
            .text-sm.mr-2 {{ displayTime(getComboAppointmentDateTime(appointment)) }}
            .text-sm.neutral500--text {{ displayDuration(getComboAppointmentDateTime(appointment)) }}
      //- Booking via
      .d-flex.align-center.mb-3
        v-icon.mr-2(size="16") $yzi-user-edit
        template(v-if="isFetching")
          v-skeleton-loader.grow.no-margin(type="text")
        template(v-else-if="isError")
          .text-sm.danger500--text Opss.. An error occured
        template(v-else)
          template(v-if="bookingDetails")
            .text-sm Booked {{ bookingDetails }}
          template(v-else)
            .text-xs.neutral500--text.italic No data
      //- Recurrences
      .d-flex.align-start.mb-3(v-if="appointment && appointment.type === 'recur'")
        v-icon.mr-2.mt-1(size="16") $yzi-repeat-03
        template(v-if="isFetching")
          v-skeleton-loader.grow.no-margin(type="text")
        template(v-else-if="isError")
          .text-sm.danger500--text Opss.. An error occured
        template(v-else)
          div
            .text-sm.mb-1 {{ displayRecurring }}
            .text-xs.underline.font-semibold.clickable(@click="openRecurringAppointmentListDialog = true") See all appointments

    v-divider

    //- Services
    .px-5.py-6
      .text-sm.font-semibold.mb-2 Services
      template(v-if="isFetching")
        v-skeleton-loader.grow(type="text@3")
      template(v-else-if="isError")
        .text-sm.danger500--text Opss.. An error occured
      template(v-else)
        //- Services name, duration and provider
        div(v-for="(service, serviceIdx) in services" :key="service.id")
          .d-flex.justify-space-between.align-center
            div
              .text-sm {{ service.name }}
              .d-flex.align-center
                .text-xs.neutral500--text.mr-1 {{ displayStartTime(getAppointmentDateTime(service.appointment)) }} ({{ displayDuration(getAppointmentDateTime(service.appointment)) }}),
                v-avatar.mr-1(color="neutral300" size="13" rounded="circle")
                  v-img(v-if="doctorProfileImage(service.provider)" width="13" height="13" :src="doctorProfileImage(service.provider)")
                  v-icon(v-else size="10") $yzi-user-03
                .text-xs.neutral500--text {{ service.provider.name }}
            .text-sm {{ service.price | displayPrice($currency) }}
          v-divider.my-2
        template(v-if="services.length > 0")
          //- Total
          .d-flex.justify-space-between.align-center
            .text-sm.font-medium Total booking fees
            template(v-if="appointment && !appointment.combo_set.length")
              .text-sm.font-medium {{ $options.filters.displayPrice((appointment.booking_fee || 0), $currency) }}
            template(v-else)
              .text-sm.font-medium {{ getComboBookingFees(appointment) | displayPrice($currency) }}
        //- Payments
        template(v-if="!hasBookingFee || !isPaid")
          .mt-6.d-flex.justify-end
            v-btn.button-sm.white--text(depressed color="neutral700" :disabled="isPaid" @click="checkRecordPayment") Record payment
        div.mt-6(v-if="hasBookingFee && isPaid")
          template(v-if="isFetchingTransaction")
            v-skeleton-loader.grow(type="text")
          template(v-else-if="isErrorTransaction")
            v-card.d-flex.align-center.pa-1.danger--border.danger500--text(outlined)
              v-icon.mr-2(size="15" color="danger500") mdi-alert-circle-outline
              .text-xs Opss.. Unable to get payment details 
          template(v-else)
            .d-flex.justify-space-between.align-center
              .text-sm.font-medium Payment - {{ transaction.payment_method }}
              .text-sm.font-medium {{ transaction.amount | displayPrice($currency) }}
        
    v-divider

    //- Notes
    .px-5.py-6
      appointment-notes(:appointment="appointment" @updated="onUpdateNotes")
    
    v-divider

    //- Preliminary questions
    template(v-if="preliminaryQuestions.length > 0")
      .px-5.py-6
        .text-sm.font-semibold.mb-2 Preliminary questions
        div.mb-2(v-for="question in preliminaryQuestions" :key="question.id")
          .text-xs.neutral500--text {{ question.name }}
          .text-sm {{ question.data }}
      
      v-divider

    //- Related Appointment
    template(v-if="hasRelatedAppointment")
      .px-5.py-6
        template(v-if="isFetchingRelatedAppointment")
          v-skeleton-loader.grow(type="text@3")
        template(v-else-if="isErrorRelatedAppointment")
          v-card.d-flex.align-center.pa-1.danger--border.danger500--text(outlined)
            v-icon.mr-2(size="15" color="danger500") mdi-alert-circle-outline
            .text-xs Opss.. Unable to get related appointment
        template(v-else)
          .text-sm.font-semibold.mb-2 Related Appointment
          //- Related appointment listing
          template(v-for="relatedAppt in relatedAppointments")
            v-card.no-border.mb-2(outlined :color="relatedAppt.color")
              div.fill-height.pl-1.related-overflow
                div.fill-height.pl-1.related-background-transparent.neutral700--text
                  //- Content
                  .pa-1.d-flex.justify-space-between.align-start
                    div
                      div.text-xs {{ relatedAppt.servicesName }}
                      .d-flex.align-center.neutral700--text.text-xxs
                        .mr-1(v-if="relatedAppt.start") {{ formatRelatedDate(relatedAppt.start) }},
                        v-avatar.mr-1(color="neutral300" size="11" rounded="circle")
                          v-img(v-if="relatedAppt.provider.profile_image" width="11" height="11" :src="relatedAppt.provider.profile_image.file")
                          v-icon(v-else size="11") $yzi-user-03
                        div {{ relatedAppt.provider.name }}
                    div(v-if="relatedAppt.type === 'recur'")
                      v-icon.ma-0.pa-0(size="13") $yzi-repeat-03
  //- Footer
  .footer.white(v-if="showFooter")
    v-divider
    .d-flex.justify-end.align-center.px-4.py-3.flex-wrap.gap-2
      v-btn.button-sm.danger500--text.rounded-lg(depressed @click="onCancel") Cancel
      v-btn.button-sm.neutral700--text.rounded-lg(depressed :disabled="isDisabledReschedule" @click="checkOpenReschedule") Reschedule
      v-btn.button-sm.neutral700--text.rounded-lg(depressed :disabled="isDisabledEdit" @click="openEditAppointmentDetailsDialog = true") Edit
      v-menu(offset-x top left attach rounded="lg")
        template(#activator="{ on }")
          v-btn.button-sm.rounded-lg.btn-icon(icon v-on="on")
            v-icon(size="16" color="neutral700") $yzi-dots-horizontal
        v-list.py-0.white
          template(v-for="(action, actidx) in actionList")
            template(v-if="action.id !== 'record' || (action.id === 'record' && !isPaid)")
              v-divider(v-if="actidx !== 0")
              v-list-item(@click="action.action" :key="'action-' + actidx")
                v-list-item-title.text-sm(:class="[action.id === 'cancel' ? 'danger500--text' : 'neutral700--text']") {{ action.text }}

  .dialogs
    add-appointment(v-if="openAddAppointmentDialog" :open.sync="openAddAppointmentDialog" @reset="$emit('updated')" @advance="openAdvanceAppointment()")
    add-advance-appointment(v-if="openAdvAppointmentDialog" :open.sync="openAdvAppointmentDialog" @reset="$emit('updated')")
    record-payment(v-if="openRecordPaymentDialog" :open.sync="openRecordPaymentDialog" :selectedAppointment="appointment" @reset="fetchAppointment")
    combo-record-payment(v-if="openComboRecordPaymentDialog" :open.sync="openComboRecordPaymentDialog" :selectedAppointment="appointment" @reset="fetchAppointment")
    reschedule-date(v-if="openRescheduleDateDialog" :open.sync="openRescheduleDateDialog" :selectedAppointment="appointment" @reset="onRescheduleDate")
    edit-appointment-details(v-if="openEditAppointmentDetailsDialog" :open.sync="openEditAppointmentDetailsDialog" :selectedAppointment="appointment" @reset="onEditAppointmentDetail")
    mark-as-arrived(v-if="openMarkAsArrivedDialog" :open.sync="openMarkAsArrivedDialog" :selectedAppointment="appointment" :register="registerMarkAsArrived" @closed="closeArrivedRegister" @reset="resetArrivedRegister" @prefill="prefillApptRegistration($event)")
    cancel-appointment(v-if="openCancelAppointmentDialog" :open.sync="openCancelAppointmentDialog" :selectedAppointment="appointment" @reset="fetchAppointment")
    follow-up-appointment(v-if="openFollowUpDialog" :open.sync="openFollowUpDialog" :selectedAppointment="appointment" @reset="$emit('updated')")
    recurring-appointment-list-dialog(v-if="openRecurringAppointmentListDialog" :open.sync="openRecurringAppointmentListDialog" :recurFor="appointment.recur_for" @view="onSelectedAppointment")
    edit-combo-appointment-dialog(v-if="openEditComboAppointmentDialog" :open.sync="openEditComboAppointmentDialog" :selectedAppointment="appointment" :comboFor="appointment.id" @closed="onRescheduleDate")
    // New register patient dialog
    register-patient-dialog(v-if="openRegisterPatientDialog" :open.sync="openRegisterPatientDialog" :existing-patient.sync="tempPatient" :prefill-appt-data="prefillApptData" @closed="closed" @downgraded="handleDowngraded")
    // Old register patient dialog (remove when not needed)
    new-register-patient-dialog(v-if="openNewRegisterPatientDialog" :open.sync="openNewRegisterPatientDialog" purpose="consultation" :existing-patient.sync="tempPatient" :prefill-appt-data="prefillApptData" @closed="closeArrivedRegister" @updated="resetArrivedRegister" @upgraded="handleUpgraded")
    // Cancelled appointments
    cancelled-appointments-dialog(v-if="openCancelledAppointmentsDialog" :open.sync="openCancelledAppointmentsDialog" :clinic-slug="company.slug" @refetch="fetchHqUnassignedAppointments")
</template>

<script>
import { DateTime, Interval } from 'luxon'
import { mapGetters, mapActions, mapState } from 'vuex'
import AppointmentNotes from '@/components/clinic/appointment/calendar/AppointmentNotes.vue'
import AddAppointment from '@/components/clinic/dialogs/AddAppointment.vue'
import AddAdvanceAppointment from '@/components/clinic/dialogs/AddAdvanceAppointment.vue'
import RecordPayment from '@/components/clinic/dialogs/RecordPayment.vue'
import ComboRecordPayment from '@/components/clinic/appointment/advance/ComboRecordPayment.vue'
import RescheduleDate from '@/components/clinic/dialogs/RescheduleDate.vue'
import EditAppointmentDetails from '@/components/clinic/dialogs/EditAppointmentDetails.vue'
import MarkAsArrived from '@/components/clinic/dialogs/MarkAsArrived.vue'
import CancelAppointment from '@/components/clinic/dialogs/CancelAppointment.vue'
import FollowUpAppointment from '@/components/clinic/dialogs/FollowUpAppointment.vue'
import AppointmentCalendarSearchDropdown from '@/components/clinic/appointment/calendar/AppointmentCalendarSearchDropdown.vue'
import RecurringAppointmentListDialog from '@/components/clinic/appointment/calendar/RecurringAppointmentListDialog.vue'
import EditComboAppointmentDialog from '@/components/clinic/appointment/advance/EditComboAppointmentDialog.vue'
import NewRegisterPatientDialog from '@/components/clinic/registration/NewRegisterPatientDialog.vue'
import RegisterPatientDialog from '@/components/clinic/registration/RegisterPatientDialog.vue'
import CancelledAppointmentsDialog from '@/components/clinic/dialogs/CancelledAppointmentsDialog.vue'

export default {
  components: {
    AppointmentNotes, AddAppointment, AddAdvanceAppointment, RecordPayment, ComboRecordPayment,
    RescheduleDate, EditAppointmentDetails, MarkAsArrived, CancelAppointment, FollowUpAppointment,
    AppointmentCalendarSearchDropdown, RecurringAppointmentListDialog, EditComboAppointmentDialog,
    NewRegisterPatientDialog, RegisterPatientDialog, CancelledAppointmentsDialog
  },
  props: {
    selectedId: {
      type: Number,
      default: null
    },
    isMobileView: {
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      isFetching: true,
      isNotFound: false,
      isError: false,
      searchText: null,
      appointment: null,
      transaction: null,
      prefillApptData: null,
      tempPatient: null,
      isFetchingTransaction: false,
      isErrorTransaction: false,
      openAddAppointmentDialog: false,
      openAdvAppointmentDialog: false,
      openRecordPaymentDialog: false,
      openComboRecordPaymentDialog: false,
      openRescheduleDateDialog: false,
      openEditComboAppointmentDialog: false,
      openEditAppointmentDetailsDialog: false,
      openMarkAsArrivedDialog: false,
      openCancelAppointmentDialog: false,
      openFollowUpDialog: false,
      openNewRegisterPatientDialog: false,
      isFetchingRelatedAppointment: false,
      isErrorRelatedAppointment: false,
      relatedAppointments: [],
      openRecurringAppointmentListDialog: false,
      openRegisterPatientDialog: false,
      openCancelledAppointmentsDialog: false,
      hqUnassignedCount: 0,
    }
  },
  watch: {
    selectedId(value) {
      if (value) {
        this.fetchAppointment()
      }
    }
  },
  mounted () {
    if (this.selectedId) {
      this.fetchAppointment()
    }
    this.fetchHqUnassignedAppointments()
  },
  computed: {
    ...mapState('user', ['member']),
    ...mapGetters({
      company: 'getCompany',
      clinic: 'clinic/getClinic',
      isHq: 'hq/getIsHq',
      hqSelectedCompany: 'hq/getSelectedCompany'
    }),
    registerMarkAsArrived () {
      return this.Auth('root', 'subscribe_cms')
    },
    actions () {
      return [
        { id: 'mark', text: this.registerMarkAsArrived ? 'Set as arrived and register' : 'Set as arrived', action: () => this.openMarkAsArrivedDialog = true },
        { id: 'reschedule', text: 'Reschedule to a new date', action: () => this.checkOpenReschedule() },
        { id: 'cancel', text: 'Cancel appointment', action: () => this.openCancelAppointmentDialog = true },
        { id: 'followup', text: 'Create follow-up appointment', action: () => this.openFollowUpDialog = true },
        { id: 'record', text: 'Record payment', action: () => this.checkRecordPayment() },
        { id: 'whatsapp', text: 'WhatsApp patient', action: () => this.onSendWhatsapp() },
      ]
    },
    patient () {
      return this.appointment?.patient || {} 
    },
    showFooter () {
      return this.selectedId && !this.isFetching && !this.isError && !this.isNotFound
    },
    bookingDetails () {
      if (this.appointment?.lead?.order_remarks?.source) {
        // Via online
        if (this.appointment.lead.order_remarks.source === 'page') {
          return 'via online'
        }
        // Via admin
        const name = this.appointment.lead?.member?.name || 'Admin'
        return `by ${name}`
      }

      return null
    },
    services () {
      if (this.appointment && this.appointment.type === 'combo' && this.appointment.combo_set.length) {
        let services = []
        this.appointment.combo_set.forEach(set => {
          const service = {
            name: set.services[0].name,
            price: set.services[0].price,
            provider: set.provider || {},
            appointment: set
          }
          services.push(service)
        })
        return services
      }
      if (this.appointment?.services.length > 0) {
        const bookingFee = this.appointment?.booking_fee ?? 0
        const service = {
          name: this.appointment.services.map(service => service.name).join(', '),
          price: bookingFee,
          provider: this.appointment?.provider ?? {},
          appointment: this.appointment
        }
        return [service]
      }

      return []
    },
    preliminaryQuestions () {
      return this.appointment?.preliminary_questions || []
    },
    hasBookingFee () {
      if (this.appointment?.booking_fee) return true

      return false
    },
    isPaid () {
      if (this.hasBookingFee) {
        return this.appointment?.paid || false
      }

      return true
    },
    actionList () {
      const upcoming = this.isHq ? ['reschedule', 'followup', 'record', 'whatsapp', 'cancel'] : ['mark', 'reschedule', 'followup', 'record', 'whatsapp', 'cancel']
      const completed = ['followup', 'record', 'whatsapp']
      const absent = this.isHq ? ['reschedule', 'followup', 'record', 'whatsapp', 'cancel'] : ['mark', 'reschedule', 'followup', 'record', 'whatsapp', 'cancel']
      const cancelled = ['reschedule', 'followup', 'record', 'whatsapp']

      switch (this.appointment?.status) {
        case 'new':
        case 'processed':
          return upcoming.map(item => { return { ...this.actions.find(action => action.id === item) } })

        case 'received':
          return completed.map(item => { return { ...this.actions.find(action => action.id === item) } })

        case 'absent':
          return absent.map(item => { return { ...this.actions.find(action => action.id === item) } })

        case 'cancelled':
          return cancelled.map(item => { return { ...this.actions.find(action => action.id === item) } })
      
        default:
          return this.actions
      }
    },
    isDisabledReschedule () {
      return this.appointment?.status === 'received'
    },
    isDisabledEdit () {
      return this.appointment?.status === 'received'
    },
    hasRelatedAppointment () {
      return (this.appointment?.recur_for || this.appointment?.combo_for) ?? false
    },
    displayRecurring () {
      if (!this.appointment.rrules) return null
      const { rrules: { freq, count, interval = null, byday = [], bymonthday = [], bysetpos = [], bymonth = [], until = null } } = this.appointment
      let result = `Repeat ${freq} on {{selectedOption}}, `
      // End time formatter
      if (until) {
        result += `end on ${DateTime.fromISO(until).toFormat('dd MMM yyyy')}`
      } else {
        result += `end after ${count} ${this.$options.filters.pluralize('time', { count })}`
      }
      // Formatting days
      let days = null
      if (byday.length > 0) {
        days = byday.map(day => this.$options.filters.humanizeDay(day, false)).join(', ')
      }
      // Daily
      if (freq === 'daily') {
        result = result.replace('{{selectedOption}}', `every ${interval} ${this.$options.filters.pluralize('day', { count: interval })}`)
      }
      // Weekly
      if (freq === 'weekly') {
        result = result.replace('{{selectedOption}}', days)
      }
      // Monthly
      if (freq === 'monthly') {
        let selectedOptions = `every {{days}} of the month`

        if (bymonthday.length > 0) {
          const monthDays = bymonthday.map(monthDay => this.$options.filters.humanizeByMonthDay(monthDay)).join(', ')
          selectedOptions = selectedOptions.replace('{{days}}', `${monthDays} day`)
        }

        if (bysetpos.length > 0) {
          const bySetPos = bysetpos[0]
          const setPos =  bySetPos < 0 ? 'last' : this.$options.filters.ordinalInWord(bysetpos[0])
          selectedOptions = selectedOptions.replace('{{days}}', `${setPos} ${days}`)
        }

        result = result.replace('{{selectedOption}}', selectedOptions)
      }
      // Yearly
      if (freq === 'yearly') {
        let selectedOptions = `{{days}} {{months}}`

        if (bymonthday.length > 0) {
          const monthDays = bymonthday.map(monthDay => this.$options.filters.humanizeByMonthDay(monthDay)).join(', ')
          selectedOptions = selectedOptions.replace('{{days}}', monthDays)
        }

        if (bymonth.length > 0) {
          const months = bymonth.map(month => this.$options.filters.getMonthFromNumber(month, false)).join(', ')
          selectedOptions = selectedOptions.replace('{{months}}', months)
        }

        result = result.replace('{{selectedOption}}', selectedOptions)
      }
      // Custom
      if (freq === 'custom') {
        result = `Repeat for ${freq} ${count} ${this.$options.filters.pluralize('time', { count })}`
      }
      
      return result
    },
    companySlug () {
      return this.isHq ? this.hqSelectedCompany.slug : this.company.slug
    }
  },
  methods: {
    ...mapActions([
      'showMessage'
    ]),
    fetchHqUnassignedAppointments () {
      const params = { cancelled_by_hq: true, cancellation_notified: false, page_size: 1, fields: 'id' }
      this.$axios.get(`/clinics/${this.company.slug}/appointments`, { params })
        .then(resp => { this.hqUnassignedCount = resp.data.count || 0 })
        .catch(() => {})
    },
    isLastItem(currentIndex, item) {
      return currentIndex === item.length - 1
    },
    async fetchAppointment () {
      try {
        this.isFetching = true
        const { data } = await this.$axios.get(`/clinics/${this.companySlug}/appointments/${this.selectedId}`)
        this.appointment = data
        // Fetch transaction
        if (this.appointment.paid) {
          this.fetchTransaction()
        } else {
          this.transaction = null
        }
        // Fetch related appointment
        if (this.appointment.recur_for || this.appointment.combo_for) {
          this.fetchRelatedAppointment()
        }
      } catch (error) {
        if (error.response && error.response.status === 404) {
          this.isNotFound = true
        } else {
          this.isError = true
        }
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.isFetching = false
      }
    },
    async fetchTransaction () {
      try {
        this.isFetchingTransaction = true
        const params = {
          lead: this.appointment.lead.id,
          company: this.isHq ? this.hqSelectedCompany.id : this.company.id,
          paid: true
        }
        const { data } = await this.$axios.get('/transactions', { params })
        if (data.count > 0) {
          this.transaction = data.results[0]
        }
      } catch (error) {
        this.isErrorTransaction = true
      } finally {
        this.isFetchingTransaction = false
      }
    },
    async fetchRelatedAppointment () {
      try {
        this.isFetchingRelatedAppointment = true
        let params = {
          parent: this.appointment.recur_for || this.appointment.combo_for
        }
        const { data } = await this.$axios.get(`/clinics/${this.companySlug}/appointments/series`, { params })
        if (data.count > 0) {
          this.relatedAppointments = data.results.map(appt => this.sanitizeRelatedAppointment(appt))
        }
      } catch (error) {
        this.isErrorRelatedAppointment = true
      } finally {
        this.isFetchingRelatedAppointment = false
      }
    },
    sanitizeRelatedAppointment (obj) {
      const newObj = { ...obj }
      // Color
      newObj.color = obj.provider?.extra_data?.tag_color ?? 'primary'
      // Services
      if (obj.combo_set.length) {
        newObj.servicesName = obj.combo_set.map(set => set?.services[0]?.name || '').join(', ')
      } else {
        newObj.servicesName = obj.services.map(service => service.name).join(', ')
      }

      return newObj
    },
    prefillApptRegistration (data) {
      this.$axios.get(`/clinics/${this.companySlug}/patients/${data.patient}`)
        .then(resp => {
          let respData = resp.data
          let prefill = {
            patient: respData
          }
          if (respData.address) {
            prefill.address = JSON.parse(JSON.stringify(respData.address))
          }
          prefill.code = data.code
          prefill.appointment = data.appointment
          prefill.provider = data.provider
          this.prefillApptData = prefill
        })
        .catch(err => {
          let message = this.$t('error_message.unable_to_complete_request')
          if (err.response.data?.detail) {
            message = err.response.data.detail
          }
          this.showMessage(['error', message])
        })
        .finally(() => {
          this.openMarkAsArrivedDialog = false
          this.openRegisterPatientDialogVersion()
        })
    },
    onCancel () {
      this.openCancelAppointmentDialog = true
    },
    onUpdateNotes (notes) {
      this.appointment.lead.remarks = notes
    },
    onRescheduleDate () {
      this.$emit('updated')
      this.fetchAppointment()
    },
    onEditAppointmentDetail () {
      this.$emit('updated')
      this.fetchAppointment()
    },
    onSelectedAppointment (appointment) {
      this.$emit('update:selectedId', appointment.id)
      this.$emit('focusSelection', appointment.datetime.from)
    },
    checkOpenReschedule () {
      if (this.appointment.type === 'combo') {
        this.openEditComboAppointmentDialog = true
      } else {
        this.openRescheduleDateDialog = true
      }
    },
    checkRecordPayment () {
      if (this.appointment.type === 'combo') {
        this.openComboRecordPaymentDialog = true
      } else {
        this.openRecordPaymentDialog = true
      }
    },
    formatRelatedDate (datetime) {
      if (!datetime) return null

      return DateTime.fromISO(datetime).toFormat('dd MMM yyyy')
    },
    openAdvanceAppointment () {
      this.openNewAppointmentDialog = false
      this.openAdvAppointmentDialog = true
    },
    getAppointmentDateTime (appointment) {
      if (appointment) {
        const from = DateTime.fromISO(appointment.start)
        const until = DateTime.fromISO(appointment.until)
        return {from, until}
      }

      return null
    },
    getComboAppointmentDateTime (appointment) {
      if (appointment && appointment.combo_set && appointment.combo_set.length) {
        let firstFrom = null
        let lastUntil =  null
        appointment.combo_set.forEach(o => {
          if (!firstFrom) {
            firstFrom = o.start
          } else if (DateTime.fromISO(firstFrom) > DateTime.fromISO(o.start)) {
            firstFrom = o.start
          }
          if (!lastUntil) {
            lastUntil = o.until
          } else if (DateTime.fromISO(lastUntil) < DateTime.fromISO(o.until)) {
            lastUntil = o.until
          }
        })
        const from = DateTime.fromISO(firstFrom)
        const until = DateTime.fromISO(lastUntil)
        return {from, until}
      }

      return null
    },
    getComboBookingFees (appointment) {
      let total = 0
      appointment.combo_set.forEach(set => {
        total += set.services[0].price
      })
      return total
    },
    closeArrivedRegister () {
      this.prefillApptData = null
      this.tempPatient = null
    },
    resetArrivedRegister () {
      this.closeArrivedRegister()
      this.fetchAppointment()
    },
    doctorProfileImage (provider) {
      if (provider.profile_image && provider.profile_image.path) {
        return `https://img.yezza.io/50x50/${provider.profile_image.path}`
      }
      return null
    },
    weekday (apptDateTime) {
      if (apptDateTime) {
        return apptDateTime.from.weekdayLong
      }

      return null
    },
    displayDate (apptDateTime) {
      if (apptDateTime) {
        return apptDateTime.from.toFormat('d MMM yyyy')
      }

      return null
    },
    displayTime (apptDateTime) {
      if (apptDateTime) {
        const format = 'h:mm a'
        return `${apptDateTime.from.toFormat(format)} - ${apptDateTime.until.toFormat(format)}`
      }

      return null
    },
    displayStartTime (apptDateTime) {
      if (apptDateTime) {
        const format = 'hh:mm'
        return apptDateTime.from.toFormat(format)
      }

      return null
    },
    displayDuration (apptDateTime) {
      if (apptDateTime) {
        const interval = Interval.fromDateTimes(apptDateTime.from, apptDateTime.until)
        const duration = interval.toDuration(['hours', 'minutes']).toObject()

        let result = ''

        if (duration.hours) result += `${duration.hours} hr`
        if (duration.minutes) result += ` ${duration.minutes} min`

        return result.trim()
      }

      return null
    },
    onSendWhatsapp () {
      const endpoint = `/leads/${this.appointment.lead.pid}/taggings/contacted`
      this.$axios.patch(endpoint)
        .then(resp => {
          this.updateActivityLog(endpoint, resp, this.appointment.lead.pid)
          const taggings = [resp.data]
          this.$set(this.appointment.lead, 'taggings', taggings)
        })
        .catch(() => {})

      let storeUrl = `https://${this.companySlug}.yezza.co/appointment`

      let message = `Hi ${this.appointment.lead.name}!%0d%0a%0d%0aWe see that you missed your appointment at ${this.$options.filters.displayTime2(this.appointment.time)}.%0d%0a%0d%0aWould you like to book another appointment? You can do so here:%0d%0a%0d%0a${storeUrl}`
      message = this.$options.filters.encodeSymbols(message)
      if (this.getMessageTemplate()) {
        message = this.getMessageTemplate()
        message = message.split('{{name}}').join(this.appointment.lead.name ? encodeURIComponent(this.appointment.lead.name) : '')
        message = message.split('{{doctor}}').join(this.appointment.provider.name ? encodeURIComponent(this.appointment.provider.name) : '')
        // message = message.split('{{doctor}}').join(this.appointment.providers.length && this.appointment.providers.map(m => m.name).join() ? encodeURIComponent(this.appointment.providers.map(m => m.name).join(', ')) : '')
        message = message.split('{{service}}').join(this.appointment.services.length && this.appointment.services.map(m => m.name).join() ? encodeURIComponent(this.appointment.services.map(m => m.name).join(', ')) : '')
        message = message.split('{{date}}').join(this.appointment.start ? encodeURIComponent(this.$options.filters.humanizeDate(this.appointment.start)) : '')
        message = message.split('{{time}}').join(this.appointment.start ? encodeURIComponent(this.$options.filters.displayTime2(this.appointment.start)) : '')
        message = message.split('{{appointment_link}}').join(storeUrl || '')
        let preAppts = this.appointment.services.filter(f => f.instructions.pre_appointment && f.instructions.pre_appointment.label)
        let postAppts = this.appointment.services.filter(f => f.instructions.post_appointment && f.instructions.post_appointment.label)
        message = message.split('{{pre_appt_instruction}}').join(encodeURIComponent(preAppts.length ? preAppts.map(m => { return m.instructions.pre_appointment.label + '\n\n' + m.instructions.pre_appointment.instructions }).join('\n\n') : ''))
        message = message.split('{{post_appt_instruction}}').join(encodeURIComponent(postAppts.length ? postAppts.map(m => { return m.instructions.post_appointment.label + '\n\n' + m.instructions.post_appointment.instructions }).join('\n\n') : ''))
        message = message.split(/\n/g).join('%0d%0a')
        message = this.$options.filters.encodeSymbols(message)
      }
      // const platform = navigator.platform || ''
      let prefix = 'api'
      // if (platform.toLowerCase().includes('win')) {
      //   prefix = 'web'
      // }
      window.open(`https://${prefix}.whatsapp.com/send?phone=${this.appointment.lead.phone}&text=${message}&app_absent=1`)
      setTimeout(() => {
        window.open(`https://${prefix}.whatsapp.com/send?phone=${this.appointment.lead.phone}&text=${message}&app_absent=1`)
      }, 200)
    },
    getMessageTemplate () {
      if (!this.clinic) {
        return null
      }
      if (this.appointment.status === 'new' || this.appointment.status === 'processed') {
        return this.clinic.upcoming_reminder
      } else if (this.appointment.status === 'absent') {
        return this.clinic.absent_reminder
      } else {
        return this.clinic.review_reminder
      }
    },
    updateActivityLog (endpoint, response, id) {
      let data = {
        company: this.company.id,
        object_id: id || null,
        object_ref: 'appointment',
        activity: this.appointmentTab === 0 ? 'Send reminder to upcoming appointment' : this.appointmentTab === 2 ? 'Send reminder to no show appointment' : 'Send reminder to review appointment' ,
        raw_data: { endpoint, response: response.data }
      }
      this.$axios.post('/users/logs', data)
        .catch(() => {})
    },
    openRegisterPatientDialogVersion () {
      if (this.member.extra_data?.new_registration) {
        this.openRegisterPatientDialog = true
        this.openNewRegisterPatientDialog = false
        return
      }
      this.openNewRegisterPatientDialog = true
      this.openRegisterPatientDialog = false
    },
    handleUpgraded () {
      this.openRegisterPatientDialogVersion()
      this.showMessage(['success', 'New registration page enabled'])
    },
    handleDowngraded () {
      this.openRegisterPatientDialogVersion()
      this.showMessage(['success', 'Old registration page enabled'])
    }
  },
}
</script>

<style scoped>
.appointment-calendar-right-panel {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

.appointment-calendar-right-panel.mobile-view {
  height: 100%;
  overflow: hidden;
}

.mobile-view .pb-15,
.mobile-view .h-screen {
  flex: 1;
  overflow-y: auto;
}

.mobile-view .footer {
  position: relative;
  flex-shrink: 0;
  padding-bottom: 12dvh;
}

.footer {
  position: sticky;
  bottom: 0;
  margin-left: 1px;
  margin-top: auto;
  box-shadow: 0px 2px 9px 0px rgba(0, 0, 0, 0.13);
}

.btn-icon {
  background-color: #f5f5f5;
}

::v-deep .v-skeleton-loader.no-margin .v-skeleton-loader__text {
  margin: 0;
}

.danger--border {
  border: 1px solid var(--v-danger);
}

.add-appointment-button {
  width: 100%;
}

.related-overflow {
  overflow-y: hidden;
}

.related-background-transparent {
  background-color: rgba(255, 255, 255, 0.5);
}

.no-border {
  border: none;
}

.unassigned-banner {
  background: #FFF7D9;
  border: 1px solid var(--v-neutral300);
  border-radius: 10px;
  padding: 8px 8px 8px 12px;
}
</style>